/**
 * Client-side file hashing for pre-deduplication (秒传).
 *
 * Uses a pure-JS MD5 implementation so we match the backend FileRegistry's
 * md5_hash column exactly, without any npm dependency.
 *
 * For large files the content is read in chunks to keep peak memory low.
 */

const HEX = '0123456789abcdef'

/**
 * Compute the MD5 hex-digest of a File object.
 * Reads the whole file into an ArrayBuffer via FileReader.
 */
export async function computeFileMD5(file) {
  const buffer = await readFileAsArrayBuffer(file)
  return md5(new Uint8Array(buffer))
}

/**
 * Compute SHA-256 hex-digest of a File (Web Crypto, native speed).
 * Useful for the vault seal or any place that needs a stronger hash.
 */
export async function computeFileSHA256(file) {
  const buffer = await file.arrayBuffer()
  const hash = await crypto.subtle.digest('SHA-256', buffer)
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// ──────────────────────────────────────────────
// Internal helpers
// ──────────────────────────────────────────────

function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target.result)
    reader.onerror = () => reject(new Error('文件读取失败'))
    reader.readAsArrayBuffer(file)
  })
}

// ──────────────────────────────────────────────
// Pure-JS MD5 (Uint8Array → hex string)
// Based on the public-domain implementation by Joseph Myers.
// ──────────────────────────────────────────────

function md5(bytes) {
  // Pre-processing: pad message to 512-bit boundary
  const len = bytes.length
  const bitLen = len * 8

  // Append 0x80, then zeros, then 64-bit little-endian length
  const padLen = (len % 64 < 56) ? (56 - len % 64) : (120 - len % 64)
  const totalLen = len + padLen + 8
  const msg = new Uint8Array(totalLen)
  msg.set(bytes)
  msg[len] = 0x80
  // Write bit-length as 64-bit LE at the end
  // JS bit-shifts are 32-bit, so we handle high/low 32 bits separately
  writeLE32(msg, totalLen - 8, bitLen >>> 0)
  writeLE32(msg, totalLen - 4, (bitLen / 0x100000000) >>> 0)

  // Process each 512-bit (64-byte) block
  let a0 = 0x67452301
  let b0 = 0xefcdab89
  let c0 = 0x98badcfe
  let d0 = 0x10325476

  const W = new Int32Array(16)

  for (let offset = 0; offset < totalLen; offset += 64) {
    // Load block into 16 little-endian 32-bit words
    for (let j = 0; j < 16; j++) {
      W[j] = readLE32(msg, offset + j * 4)
    }

    let A = a0, B = b0, C = c0, D = d0

    // Round 1
    A = ff(A, B, C, D, W[0],  7,  0xd76aa478); D = ff(D, A, B, C, W[1],  12, 0xe8c7b756)
    C = ff(C, D, A, B, W[2],  17, 0x242070db); B = ff(B, C, D, A, W[3],  22, 0xc1bdceee)
    A = ff(A, B, C, D, W[4],  7,  0xf57c0faf); D = ff(D, A, B, C, W[5],  12, 0x4787c62a)
    C = ff(C, D, A, B, W[6],  17, 0xa8304613); B = ff(B, C, D, A, W[7],  22, 0xfd469501)
    A = ff(A, B, C, D, W[8],  7,  0x698098d8); D = ff(D, A, B, C, W[9],  12, 0x8b44f7af)
    C = ff(C, D, A, B, W[10], 17, 0xffff5bb1); B = ff(B, C, D, A, W[11], 22, 0x895cd7be)
    A = ff(A, B, C, D, W[12], 7,  0x6b901122); D = ff(D, A, B, C, W[13], 12, 0xfd987193)
    C = ff(C, D, A, B, W[14], 17, 0xa679438e); B = ff(B, C, D, A, W[15], 22, 0x49b40821)

    // Round 2
    A = gg(A, B, C, D, W[1],  5,  0xf61e2562); D = gg(D, A, B, C, W[6],  9,  0xc040b340)
    C = gg(C, D, A, B, W[11], 14, 0x265e5a51); B = gg(B, C, D, A, W[0],  20, 0xe9b6c7aa)
    A = gg(A, B, C, D, W[5],  5,  0xd62f105d); D = gg(D, A, B, C, W[10], 9,  0x02441453)
    C = gg(C, D, A, B, W[15], 14, 0xd8a1e681); B = gg(B, C, D, A, W[4],  20, 0xe7d3fbc8)
    A = gg(A, B, C, D, W[9],  5,  0x21e1cde6); D = gg(D, A, B, C, W[14], 9,  0xc33707d6)
    C = gg(C, D, A, B, W[3],  14, 0xf4d50d87); B = gg(B, C, D, A, W[8],  20, 0x455a14ed)
    A = gg(A, B, C, D, W[13], 5,  0xa9e3e905); D = gg(D, A, B, C, W[2],  9,  0xfcefa3f8)
    C = gg(C, D, A, B, W[7],  14, 0x676f02d9); B = gg(B, C, D, A, W[12], 20, 0x8d2a4c8a)

    // Round 3
    A = hh(A, B, C, D, W[5],  4,  0xfffa3942); D = hh(D, A, B, C, W[8],  11, 0x8771f681)
    C = hh(C, D, A, B, W[11], 16, 0x6d9d6122); B = hh(B, C, D, A, W[14], 23, 0xfde5380c)
    A = hh(A, B, C, D, W[1],  4,  0xa4beea44); D = hh(D, A, B, C, W[4],  11, 0x4bdecfa9)
    C = hh(C, D, A, B, W[7],  16, 0xf6bb4b60); B = hh(B, C, D, A, W[10], 23, 0xbebfbc70)
    A = hh(A, B, C, D, W[13], 4,  0x289b7ec6); D = hh(D, A, B, C, W[0],  11, 0xeaa127fa)
    C = hh(C, D, A, B, W[3],  16, 0xd4ef3085); B = hh(B, C, D, A, W[6],  23, 0x04881d05)
    A = hh(A, B, C, D, W[9],  4,  0xd9d4d039); D = hh(D, A, B, C, W[12], 11, 0xe6db99e5)
    C = hh(C, D, A, B, W[15], 16, 0x1fa27cf8); B = hh(B, C, D, A, W[2],  23, 0xc4ac5665)

    // Round 4
    A = ii(A, B, C, D, W[0],  6,  0xf4292244); D = ii(D, A, B, C, W[7],  10, 0x432aff97)
    C = ii(C, D, A, B, W[14], 15, 0xab9423a7); B = ii(B, C, D, A, W[5],  21, 0xfc93a039)
    A = ii(A, B, C, D, W[12], 6,  0x655b59c3); D = ii(D, A, B, C, W[3],  10, 0x8f0ccc92)
    C = ii(C, D, A, B, W[10], 15, 0xffeff47d); B = ii(B, C, D, A, W[1],  21, 0x85845dd1)
    A = ii(A, B, C, D, W[8],  6,  0x6fa87e4f); D = ii(D, A, B, C, W[15], 10, 0xfe2ce6e0)
    C = ii(C, D, A, B, W[6],  15, 0xa3014314); B = ii(B, C, D, A, W[13], 21, 0x4e0811a1)
    A = ii(A, B, C, D, W[4],  6,  0xf7537e82); D = ii(D, A, B, C, W[11], 10, 0xbd3af235)
    C = ii(C, D, A, B, W[2],  15, 0x2ad7d2bb); B = ii(B, C, D, A, W[9],  21, 0xeb86d391)

    a0 = (a0 + A) | 0
    b0 = (b0 + B) | 0
    c0 = (c0 + C) | 0
    d0 = (d0 + D) | 0
  }

  return toHex(a0) + toHex(b0) + toHex(c0) + toHex(d0)
}

// ── MD5 round functions ──

function ff(a, b, c, d, x, s, t) {
  return cmn((b & c) | (~b & d), a, b, x, s, t)
}

function gg(a, b, c, d, x, s, t) {
  return cmn((b & d) | (c & ~d), a, b, x, s, t)
}

function hh(a, b, c, d, x, s, t) {
  return cmn(b ^ c ^ d, a, b, x, s, t)
}

function ii(a, b, c, d, x, s, t) {
  return cmn(c ^ (b | ~d), a, b, x, s, t)
}

function cmn(q, a, b, x, s, t) {
  a = (((a + q) | 0) + ((x + t) | 0)) | 0
  return (((a << s) | (a >>> (32 - s))) + b) | 0
}

// ── Byte-level helpers ──

function readLE32(buf, offset) {
  return (
    buf[offset] |
    (buf[offset + 1] << 8) |
    (buf[offset + 2] << 16) |
    (buf[offset + 3] << 24)
  )
}

function writeLE32(buf, offset, value) {
  buf[offset]     = value & 0xff
  buf[offset + 1] = (value >>> 8) & 0xff
  buf[offset + 2] = (value >>> 16) & 0xff
  buf[offset + 3] = (value >>> 24) & 0xff
}

function toHex(n) {
  let s = ''
  for (let i = 0; i < 4; i++) {
    s += HEX.charAt((n >> (i * 8 + 4)) & 0xf) + HEX.charAt((n >> (i * 8)) & 0xf)
  }
  return s
}
